import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Full-screen tap-to-zoom lightbox for product images.
 * - Tap the image to toggle zoom (1x ↔ 2.6x).
 * - While zoomed, move the pointer / finger to pan around (transform-origin
 *   follows the cursor) — works on both desktop and touch.
 * - Tap the backdrop or the X button to close.
 */
export default function ImageZoomModal({ open, onClose, src, alt = "", lang = "ar" }) {
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const ar = lang === "ar";

  if (!open || !src) return null;

  const move = (clientX, clientY, rect) => {
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    setOrigin({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const close = () => { setZoomed(false); onClose?.(); };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center"
         style={{ background: "rgba(6,15,12,0.92)", backdropFilter: "blur(6px)" }}
         data-testid="image-zoom-modal" onClick={close}>
      <button onClick={close} aria-label={ar ? "إغلاق" : "Close"}
              className="absolute top-4 w-11 h-11 rounded-full flex items-center justify-center text-white transition-transform active:scale-90"
              style={{ background: "rgba(255,255,255,0.15)", insetInlineEnd: 16, zIndex: 10 }}
              data-testid="image-zoom-close">
        <X size={22} />
      </button>

      <div className="absolute top-5 flex items-center gap-1.5 text-white/70 text-xs pointer-events-none"
           style={{ insetInlineStart: 16, zIndex: 10 }}>
        {zoomed ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
        <span>
          {ar
            ? (zoomed ? "اضغط للتصغير · حرّك للتنقّل" : "اضغط على الصورة للتكبير")
            : (zoomed ? "Tap to zoom out · move to pan" : "Tap image to zoom")}
        </span>
      </div>

      <img
        src={src} alt={alt} draggable={false}
        onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
        onMouseMove={(e) => { if (zoomed) move(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect()); }}
        onTouchMove={(e) => {
          if (zoomed && e.touches[0]) {
            const tch = e.touches[0];
            move(tch.clientX, tch.clientY, e.currentTarget.getBoundingClientRect());
          }
        }}
        className="select-none"
        style={{
          maxWidth: "92vw", maxHeight: "86vh", objectFit: "contain",
          transform: zoomed ? "scale(2.6)" : "scale(1)",
          transformOrigin: `${origin.x}% ${origin.y}%`,
          transition: "transform 0.25s ease",
          cursor: zoomed ? "zoom-out" : "zoom-in",
          borderRadius: 12,
        }}
        data-testid="image-zoom-img"
      />
    </div>,
    document.body
  );
}
